import React from "react";
import { useSearchParams } from "react-router-dom";
import { fetchReportFormAsync } from "@core/reports/api/fetchReportForm";
import {
  fetchDepartmentReportsQueryAsync,
  type DepartmentReportListItem,
} from "@core/reports/api/fetchDepartmentReportsQuery";
import { requestReportApproveAsync } from "@core/reports/api/requestReportApprove";
import { requestReportReviewAsync } from "@core/reports/api/requestReportReview";
import { requestDepartmentReportSaveAsync } from "@core/reports/api/requestDepartmentReportSave";
import { requestDepartmentReportSubmitAsync } from "@core/reports/api/requestDepartmentReportSubmit";
import { processDepartmentReportAsync } from "@core/reports/api/processDepartmentReport";
import type {
  PaginatedReportResponse,
  ReportProcessResult,
  DepartmentReportMediaItem,
  DepartmentReportDocument,
  SubmissionSummary,
  ReportFormSection,
} from "@core/reports/api/types";
import { toTreeColumnsSpec, buildTreeByScope, buildTreeForSection } from "@core/reports/adapters/reportTreeAdapters";
import { buildPrintHeaderFields, buildReportFileBase } from "@core/reports/utils/reportPrintMeta";
import { useReportTags } from "@features/report/tag/hooks/useReportTags";
import { useDepartmentReportMedia } from "@core/reports/hooks/useDepartmentReportMedia";
import { getPaginationRange } from "@core/components/TablePaginationFooter";
import {
  canApproveDepartmentReport,
  canValidateDepartmentReport,
  getApprovalNotes,
  getNeedsAttentionReason,
  parseNeedsAttentionDetails,
  isCurrentUserSubmitter,
  isDepartmentReportEditable,
  isDepartmentReportMissing,
  isTagClosed,
  isTagOpen,
  normalizeReportWorkflowStatus,
  resolveDepartmentReportDisplayStatus,
  type ReportProcessResults,
} from "@core/reports/utils/reportWorkflow";
import { useReportActionPermissions } from "@core/reports/hooks/useReportActionPermissions";
import {
  buildReportPermissionFlags,
  type ReportPermissionFlags,
} from "@core/reports/utils/reportPermissionFlags";
import { surfaceDeptActionError } from "@core/reports/utils/surfaceDeptActionError";
import { useAuthStore } from "@features/auth/store/authStore";
import { orderReportSectionViews } from "@core/reports/utils/orderReportSectionViews";
import {
  collectAllLensApiParams,
  formatActiveLensesFileSuffix,
  formatActiveLensesSubtitle,
  parseReportSectionView,
  readActiveReportSection,
  readSectionLens,
  writeActiveReportSection,
  writeSectionLens,
  type ReportSectionViewLensParams,
  type SectionLensKey,
} from "@core/reports/utils/reportSectionViewLens";

export type ReportScope = "church" | "district" | "nation";

/**
 * Maps a scope + entity id to the discriminated `{ scope, <idField> }` part the report APIs expect.
 */
function scopeIdPart(scope: ReportScope, entityId: number) {
  switch (scope) {
    case "church":
      return { scope, churchId: entityId } as const;
    case "district":
      return { scope, districtId: entityId } as const;
    default:
      return { scope, nationId: entityId } as const;
  }
}

export interface UseReportPageConfig {
  scope: ReportScope;
  entityId: number;
  departmentId: number;
  reportTagId: number;
}

export function useReportPage(config: UseReportPageConfig) {
  const { scope, entityId, departmentId, reportTagId } = config;

  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get("page")) || 1;
  const perPage = Number(searchParams.get("perPage")) || 20;
  const officePage = Number(searchParams.get("officePage")) || 1;
  const districtOriginPage = Number(searchParams.get("districtOriginPage")) || 1;

  const [resp, setResp] = React.useState<PaginatedReportResponse | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [deptReport, setDeptReport] = React.useState<DepartmentReportListItem | null>(null);
  const [deptReportLoading, setDeptReportLoading] = React.useState(false);

  // Department-report action state
  const [deptNotes, setDeptNotes] = React.useState("");
  const [savingDraft, setSavingDraft] = React.useState(false);
  const [submittingDept, setSubmittingDept] = React.useState(false);
  const [validatingDept, setValidatingDept] = React.useState(false);
  const [deptActionError, setDeptActionError] = React.useState<string | null>(null);
  const [validationResult, setValidationResult] = React.useState<ReportProcessResult | null>(null);

  // Approve modal
  const [showApproveModal, setShowApproveModal] = React.useState(false);
  const [approveNotes, setApproveNotes] = React.useState("");
  const [approving, setApproving] = React.useState(false);
  const [approveError, setApproveError] = React.useState<string | null>(null);

  // Request review modal
  const [showReviewModal, setShowReviewModal] = React.useState(false);
  const [requestingReview, setRequestingReview] = React.useState(false);
  const [reviewError, setReviewError] = React.useState<string | null>(null);

  const { data: tags = [], loading: tagsLoading } = useReportTags({
    ...(scope === "church"
      ? { churchId: entityId || undefined }
      : scope === "district"
        ? { districtId: entityId || undefined }
        : { nationId: entityId || undefined }),
    all: true,
  });

  const canLoad = entityId > 0 && departmentId > 0 && reportTagId > 0;

  const onPageChange = React.useCallback(
    (nextPage: number) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", String(nextPage));
        return next;
      });
    },
    [setSearchParams]
  );

  const onOfficePageChange = React.useCallback(
    (nextPage: number) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("officePage", String(nextPage));
        return next;
      });
    },
    [setSearchParams]
  );

  const onDistrictOriginPageChange = React.useCallback(
    (nextPage: number) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("districtOriginPage", String(nextPage));
        return next;
      });
    },
    [setSearchParams]
  );

  const load = React.useCallback(async (): Promise<void> => {
    if (!canLoad) return;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchReportFormAsync({
        ...scopeIdPart(scope, entityId),
        departmentId,
        reportTagId,
        page,
        perPage,
        ...(scope === "district" || scope === "nation" ? { officePage } : {}),
        ...(scope === "nation" ? { districtOriginPage } : {}),
        lensParams: collectAllLensApiParams(searchParams),
      } as Parameters<typeof fetchReportFormAsync>[0]);
      if (result.isSuccess()) {
        setResp(result.getData());
      } else {
        setError(result.getError()?.detail || "Failed to fetch report");
        setResp(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unknown error");
      setResp(null);
    } finally {
      setLoading(false);
    }
  }, [
    scope,
    entityId,
    departmentId,
    reportTagId,
    page,
    perPage,
    officePage,
    districtOriginPage,
    canLoad,
    searchParams,
  ]);

  React.useEffect(() => {
    if (canLoad) load();
  }, [load, canLoad]);

  const loadDepartmentReport = React.useCallback(async (): Promise<void> => {
    if (entityId <= 0 || departmentId <= 0) {
      setDeptReport(null);
      return;
    }
    setDeptReportLoading(true);
    try {
      const result = await fetchDepartmentReportsQueryAsync({
        ...scopeIdPart(scope, entityId),
        departmentId,
        reportTagId: reportTagId || undefined,
        page: 1,
        perPage: 50,
      } as Parameters<typeof fetchDepartmentReportsQueryAsync>[0]);
      if (!result.isSuccess()) {
        setDeptReport(null);
        return;
      }
      const items = result.getData()?.data ?? [];
      setDeptReport(items.find((x) => x.department_id === departmentId) ?? null);
    } finally {
      setDeptReportLoading(false);
    }
  }, [scope, entityId, departmentId, reportTagId]);

  React.useEffect(() => {
    if (canLoad) loadDepartmentReport();
  }, [canLoad, loadDepartmentReport]);

  const meta = resp?.data?.meta;
  const submission = (meta as any)?.department_submission ?? null;
  const submissionSummary: SubmissionSummary | null =
    (meta as { submission_summary?: SubmissionSummary | null } | undefined)
      ?.submission_summary ?? null;
  const formSections = React.useMemo(() => resp?.data?.sections ?? [], [resp]);
  const officeSection = formSections.find((s) => s.type === "office") ?? null;
  const childrenSection = formSections.find((s) => s.type === "children") ?? null;
  const { start: resultStart, end: resultEnd } = getPaginationRange({
    page: resp?.current_page ?? page,
    perPage: resp?.per_page ?? perPage,
    total: resp?.total ?? 0,
    currentCount: resp?.data?.rows?.length ?? 0,
  });

  const departmentReportId: number | null =
    submission?.department_report_id ?? deptReport?.department_report_id ?? null;

  const isMissingReport = isDepartmentReportMissing(departmentReportId);

  const statusName = resolveDepartmentReportDisplayStatus(
    departmentReportId,
    submission?.report_status_name ?? deptReport?.department_report_status_name ?? null
  );
  const processResults = (meta as { process_results?: ReportProcessResults | null } | undefined)
    ?.process_results;
  const needsAttentionReason = getNeedsAttentionReason(statusName, processResults);
  const needsAttentionDetails = parseNeedsAttentionDetails(processResults);
  const approvalNotesDisplay = getApprovalNotes(
    submission?.approval_notes,
    processResults
  );
  const notesEditable = isDepartmentReportEditable(statusName);
  const currentUserId = useAuthStore((s) => s.getAuthUser()?.id ?? null);
  const isOwnReport = isCurrentUserSubmitter(currentUserId, submission?.submitted_by);

  const selectedTag = tags.find((t) => t.report_tag_id === reportTagId);
  const tagStatusName = selectedTag?.report_tag_status_name ?? null;
  // Unknown/loading → allow UI writes (same as former isTagClosed-only gate); known non-OPEN → block.
  const tagIsOpen =
    tagStatusName == null || tagStatusName === ""
      ? true
      : isTagOpen(tagStatusName);
  const tagIsClosed = isTagClosed(tagStatusName);
  const writesAllowedByTag = tagIsOpen;

  const {
    loading: permissionsLoading,
    canSave,
    canSubmit,
    canReview,
    canApprove,
    canApproveOwn,
    canValidate,
    canUploadMedia,
    canRemoveMedia,
    canReviewActivity,
  } = useReportActionPermissions({
    scope,
    scopeId: entityId,
    departmentId,
    includeActivityEntry: true,
    enabled: entityId > 0 && departmentId > 0,
  });

  const permissionFlags: ReportPermissionFlags = React.useMemo(
    () =>
      buildReportPermissionFlags(
        notesEditable && writesAllowedByTag,
        {
          canSave,
          canSubmit,
          canReview,
          canApprove,
          canApproveOwn,
          canValidate,
          canUploadMedia,
          canRemoveMedia,
        },
        {
          canValidateByWorkflow: canValidateDepartmentReport(statusName),
          canApproveByWorkflow: canApproveDepartmentReport(statusName),
          canManualValidateByWorkflow:
            normalizeReportWorkflowStatus(statusName) === "SUBMITTED",
          isOwnReport,
        }
      ),
    [
      notesEditable,
      writesAllowedByTag,
      canSave,
      canSubmit,
      canReview,
      canApprove,
      canApproveOwn,
      canValidate,
      canUploadMedia,
      canRemoveMedia,
      statusName,
      isOwnReport,
    ]
  );

  const showPaging = Boolean(
    formSections.length === 0 &&
      resp &&
      (typeof resp.total === "number"
        ? resp.total > perPage
        : !!(resp.next_page_url || resp.prev_page_url))
  );

  const existingMedia = React.useMemo<DepartmentReportMediaItem[]>(
    () => (Array.isArray((meta as any)?.media) ? ((meta as any).media as DepartmentReportMediaItem[]) : []),
    [meta]
  );
  const mediaRequirements = React.useMemo(
    () =>
      Array.isArray((meta as any)?.documents)
        ? ((meta as any).documents as DepartmentReportDocument[])
        : [],
    [meta]
  );
  const media = useDepartmentReportMedia({
    scope,
    entityId,
    departmentId,
    reportTagId,
    notes: deptNotes,
    departmentReportId,
    existingMedia,
    editable: permissionFlags.fieldsEditable && canUploadMedia,
    onChanged: async () => {
      await load();
      await loadDepartmentReport();
    },
  });

  // Keep notes in sync with backend when selection changes (not mid-save).
  React.useEffect(() => {
    if (savingDraft || submittingDept) return;
    setDeptNotes(String(submission?.notes ?? ""));
  }, [submission?.notes, submission, savingDraft, submittingDept]);

  React.useEffect(() => {
    setDeptActionError(null);
    setValidationResult(null);
    media.clearUploadDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, departmentId, reportTagId]);

  React.useEffect(() => {
    if (!showApproveModal) {
      setApproveNotes("");
      setApproveError(null);
    }
  }, [showApproveModal]);

  React.useEffect(() => {
    if (!showReviewModal) {
      setReviewError(null);
    }
  }, [showReviewModal]);

  const columnsSpec = React.useMemo(() => toTreeColumnsSpec(resp?.data?.columns ?? []), [resp]);
  const treeData = React.useMemo(() => buildTreeByScope(scope, resp?.data?.rows ?? []), [scope, resp]);

  const sectionViews = React.useMemo(() => {
    return formSections.map((section) => {
      const rows = "rows" in section ? section.rows ?? [] : [];
      const columns = "columns" in section ? section.columns ?? [] : [];
      return {
        section,
        columnsSpec: toTreeColumnsSpec(columns),
        tree: buildTreeForSection(section.type, scope, rows),
        totals: "totals" in section ? section.totals ?? null : null,
        currentPage: "current_page" in section ? section.current_page ?? 1 : 1,
        lastPage: "last_page" in section ? section.last_page ?? 1 : 1,
        perPage: "per_page" in section ? section.per_page ?? perPage : perPage,
        total: "total" in section ? section.total ?? rows.length : rows.length,
      };
    });
  }, [formSections, scope, perPage]);

  // Download the full (all-rows) report as a landscape PDF. Approved reports only (gated in the UI).
  const [downloadingPdf, setDownloadingPdf] = React.useState(false);
  const handleDownloadPdf = React.useCallback(async () => {
    if (!canLoad) return;
    setDownloadingPdf(true);
    setDeptActionError(null);
    try {
      const result = await fetchReportFormAsync({
        ...scopeIdPart(scope, entityId),
        departmentId,
        reportTagId,
        page: 1,
        perPage: 100000,
        ...(scope === "district" || scope === "nation" ? { officePage: 1 } : {}),
        ...(scope === "nation" ? { districtOriginPage: 1 } : {}),
        lensParams: collectAllLensApiParams(searchParams),
      } as Parameters<typeof fetchReportFormAsync>[0]);
      if (!result.isSuccess()) {
        throw new Error(result.getError()?.detail || "Failed to load report for download.");
      }
      const full = result.getData();
      const fullMeta = full?.data?.meta;
      const fullNotes = (fullMeta as any)?.department_submission?.notes ?? null;
      const fullMedia = Array.isArray((fullMeta as any)?.media) ? ((fullMeta as any).media as DepartmentReportMediaItem[]) : [];
      const pdfAttachments = fullMedia.map((item) => ({
        typeName: item.document_name,
        caption: item.description,
        filename: item.filename,
      }));
      const fields = buildPrintHeaderFields(fullMeta, scope);
      const departmentName = fields.departmentName !== "—" ? fields.departmentName : "Department";
      const title = `${departmentName} Report`;
      const lensSubtitle = formatActiveLensesSubtitle(searchParams);
      const lensFileSuffix = formatActiveLensesFileSuffix(searchParams);
      const fileName = [buildReportFileBase(fields, `${departmentName} Report`), lensFileSuffix]
        .filter(Boolean)
        .join(" - ");
      const pdfSections = (full?.data?.sections ?? []).map((section: ReportFormSection) => {
        const sectionKey = (
          section.type === "office"
            ? "office"
            : section.type === "district_origin"
              ? "district_origin"
              : "children"
        ) as SectionLensKey;
        const lens = readSectionLens(searchParams, sectionKey);
        const rows = "rows" in section ? section.rows ?? [] : [];
        const columns = "columns" in section ? section.columns ?? [] : [];
        const nameHeader =
          section.type === "office"
            ? "Report Template"
            : section.type === "district_origin"
              ? "District"
              : scope === "nation"
                ? "District"
                : "Church";
        if (lens.view === "timeline") {
          return {
            title: section.label,
            nameHeader,
            columnsSpec: toTreeColumnsSpec(columns),
            tree: [],
            totals: null,
            layout: "timeline" as const,
            timelineEvents: Array.isArray(section.timeline_events) ? section.timeline_events : [],
          };
        }
        return {
          title: section.label,
          nameHeader,
          columnsSpec: toTreeColumnsSpec(columns),
          tree: buildTreeForSection(section.type, scope, rows),
          totals: "totals" in section ? section.totals ?? null : null,
        };
      });

      const churchLens = readSectionLens(searchParams, "church");
      const churchIsTimeline = scope === "church" && churchLens.view === "timeline";
      const timelineEvents = full?.data?.timeline_events;
      const { exportDepartmentReportPdf } = await import("@core/reports/pdf/exportDepartmentReportPdf");
      await exportDepartmentReportPdf({
        fields,
        title,
        statusName,
        fileName,
        nameHeader: scope === "nation" ? "District" : scope === "district" ? "Church" : "Report Template",
        columnsSpec: toTreeColumnsSpec(full?.data?.columns ?? []),
        tree: churchIsTimeline ? [] : buildTreeByScope(scope, full?.data?.rows ?? []),
        totals: churchIsTimeline ? null : full?.data?.totals ?? null,
        notes: fullNotes,
        attachments: pdfAttachments.length > 0 ? pdfAttachments : undefined,
        lensSubtitle,
        sections:
          pdfSections.length > 0
            ? pdfSections
            : churchIsTimeline
              ? [
                  {
                    title,
                    nameHeader: "Report Template",
                    columnsSpec: toTreeColumnsSpec(full?.data?.columns ?? []),
                    tree: [],
                    layout: "timeline",
                    timelineEvents: Array.isArray(timelineEvents) ? timelineEvents : [],
                  },
                ]
              : undefined,
      });
    } catch (e) {
      surfaceDeptActionError(
        e instanceof Error ? e.message : "Failed to generate PDF.",
        setDeptActionError
      );
    } finally {
      setDownloadingPdf(false);
    }
  }, [canLoad, scope, entityId, departmentId, reportTagId, statusName, searchParams]);

  const handleSaveDraft = React.useCallback(async () => {
    if (!canLoad) return;
    setDeptActionError(null);
    setSavingDraft(true);
    try {
      media.clearUploadDraft();
      const result = await requestDepartmentReportSaveAsync({
        ...scopeIdPart(scope, entityId),
        departmentId,
        reportTagId,
        notes: deptNotes,
        existingMedia: media.captionPatches,
      });
      if (!result.isSuccess()) throw new Error(result.getError()?.detail || "Failed to save draft.");
      await load();
      await loadDepartmentReport();
    } catch (e) {
      surfaceDeptActionError(
        e instanceof Error ? e.message : "Failed to save draft.",
        setDeptActionError
      );
    } finally {
      setSavingDraft(false);
    }
  }, [scope, entityId, departmentId, reportTagId, deptNotes, media, canLoad, load, loadDepartmentReport]);

  const handleSubmitDepartmentReport = React.useCallback(async () => {
    if (!canLoad) return;
    setDeptActionError(null);
    setSubmittingDept(true);
    try {
      media.clearUploadDraft();
      const result = await requestDepartmentReportSubmitAsync({
        ...scopeIdPart(scope, entityId),
        departmentId,
        reportTagId,
        notes: deptNotes,
        existingMedia: media.captionPatches,
      });
      if (!result.isSuccess()) throw new Error(result.getError()?.detail || "Failed to submit report.");
      await load();
      await loadDepartmentReport();
    } catch (e) {
      surfaceDeptActionError(
        e instanceof Error ? e.message : "Failed to submit report.",
        setDeptActionError
      );
    } finally {
      setSubmittingDept(false);
    }
  }, [scope, entityId, departmentId, reportTagId, deptNotes, media, canLoad, load, loadDepartmentReport]);

  const handleValidateDepartmentReport = React.useCallback(async () => {
    if (!canLoad) return;
    setDeptActionError(null);
    setValidatingDept(true);
    try {
      const result = await processDepartmentReportAsync({
        ...scopeIdPart(scope, entityId),
        departmentId,
        reportTagId,
      });
      if (!result.isSuccess()) throw new Error(result.getError()?.detail || "Failed to validate report.");
      setValidationResult(result.getData());
      await load();
      await loadDepartmentReport();
    } catch (e) {
      surfaceDeptActionError(
        e instanceof Error ? e.message : "Failed to validate report.",
        setDeptActionError
      );
    } finally {
      setValidatingDept(false);
    }
  }, [scope, entityId, departmentId, reportTagId, canLoad, load, loadDepartmentReport]);

  const handleApprove = React.useCallback(async () => {
    if (!canLoad) return;
    if (!departmentReportId) {
      setApproveError("Missing department report id. Cannot approve report.");
      return;
    }
    setApproving(true);
    setApproveError(null);
    try {
      const result = await requestReportApproveAsync({
        ...scopeIdPart(scope, entityId),
        departmentId,
        departmentReportId: Number(departmentReportId),
        notes: approveNotes ?? "",
      });
      if (!result.isSuccess()) throw new Error(result.getError()?.detail || "Failed to approve report.");
      setShowApproveModal(false);
      await load();
      await loadDepartmentReport();
    } catch (e) {
      setApproveError(e instanceof Error ? e.message : "Failed to approve report.");
    } finally {
      setApproving(false);
    }
  }, [scope, entityId, departmentId, departmentReportId, approveNotes, canLoad, load, loadDepartmentReport]);

  const handleRequestReview = React.useCallback(
    async ({ reason }: { reason: string }) => {
      if (!canLoad) return;
      if (!departmentReportId) {
        setReviewError("Missing department report id. Cannot request review.");
        return;
      }
      setRequestingReview(true);
      setReviewError(null);
      try {
        const result = await requestReportReviewAsync({
          ...scopeIdPart(scope, entityId),
          departmentId,
          departmentReportId: Number(departmentReportId),
          notes: reason.slice(0, 255),
        });
        if (!result.isSuccess()) {
          throw new Error(result.getError()?.detail || "Failed to request review.");
        }
        setShowReviewModal(false);
        await load();
        await loadDepartmentReport();
      } catch (e) {
        setReviewError(e instanceof Error ? e.message : "Failed to request review.");
      } finally {
        setRequestingReview(false);
      }
    },
    [scope, entityId, departmentId, departmentReportId, canLoad, load, loadDepartmentReport]
  );

  const setSectionLens = React.useCallback(
    (section: SectionLensKey, lens: ReportSectionViewLensParams) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        writeSectionLens(next, section, {
          view: parseReportSectionView(lens.view),
          bucket: lens.bucket,
          from: lens.from,
          to: lens.to,
        });
        return next;
      });
    },
    [setSearchParams]
  );

  const allowedSectionTypes = React.useMemo(() => {
    const ordered = orderReportSectionViews(formSections);
    return ordered.map((s) => s.type);
  }, [formSections]);

  const activeSection = readActiveReportSection(searchParams, allowedSectionTypes);

  const setActiveSection = React.useCallback(
    (sectionType: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        writeActiveReportSection(next, sectionType, allowedSectionTypes);
        return next;
      });
    },
    [allowedSectionTypes, setSearchParams]
  );

  return {
    // identifiers
    scope,
    entityId,
    departmentId,
    reportTagId,
    // data
    resp,
    loading,
    error,
    meta,
    submission,
    submissionSummary,
    officeSection,
    childrenSection,
    sections: formSections,
    sectionViews,
    statusName,
    needsAttentionReason,
    needsAttentionDetails,
    approvalNotesDisplay,
    departmentReportId,
    isMissingReport,
    canLoad,
    showPaging,
    notesEditable,
    permissionFlags,
    permissionsLoading,
    canReviewActivity,
    selectedTag,
    tagStatusName,
    tagIsOpen,
    tagIsClosed,
    columnsSpec,
    treeData,
    existingMedia,
    mediaRequirements,
    media,
    tags,
    tagsLoading,
    // pagination
    page,
    perPage,
    officePage,
    districtOriginPage,
    onPageChange,
    onOfficePageChange,
    onDistrictOriginPageChange,
    resultStart,
    resultEnd,
    // notes + actions
    deptNotes,
    setDeptNotes,
    savingDraft,
    submittingDept,
    validatingDept,
    deptActionError,
    validationResult,
    deptReportLoading,
    downloadingPdf,
    handleDownloadPdf,
    load,
    loadDepartmentReport,
    handleSaveDraft,
    handleSubmitDepartmentReport,
    handleValidateDepartmentReport,
    // approve modal
    showApproveModal,
    setShowApproveModal,
    approveNotes,
    setApproveNotes,
    approving,
    approveError,
    handleApprove,
    // request review modal
    showReviewModal,
    setShowReviewModal,
    requestingReview,
    reviewError,
    setReviewError,
    handleRequestReview,
    setSectionLens,
    activeSection,
    setActiveSection,
    searchParams,
  };
}

export type UseReportPageResult = ReturnType<typeof useReportPage>;
